import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Common IANA timezones for the dropdown — we accept any valid IANA value
const COMMON_TZS = [
  "Africa/Johannesburg", "Africa/Nairobi", "Africa/Lagos", "Africa/Cairo",
  "Asia/Dubai", "Asia/Riyadh", "Asia/Karachi", "Asia/Kolkata",
  "Asia/Singapore", "Asia/Hong_Kong", "Asia/Tokyo",
  "Europe/London", "Europe/Berlin", "Europe/Paris", "Europe/Istanbul",
  "America/New_York", "America/Chicago", "America/Los_Angeles",
  "America/Sao_Paulo", "Australia/Sydney", "Pacific/Auckland", "UTC",
];

export const WhatsAppCard = ({ code }) => {
  const [number, setNumber] = useState("");
  const [tz, setTz] = useState(
    (Intl.DateTimeFormat().resolvedOptions().timeZone) || "Africa/Johannesburg"
  );
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const loadStatus = async () => {
    if (!code) return;
    try {
      const { data } = await axios.get(`${API}/terminal/whatsapp/status`, {
        params: { code },
      });
      setStatus(data);
      if (data.timezone) setTz(data.timezone);
    } catch {}
  };

  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, [code]);

  const save = async (e) => {
    e.preventDefault();
    setErr(null);
    setMsg(null);
    setBusy("save");
    try {
      await axios.post(`${API}/terminal/whatsapp/set`, {
        code,
        whatsapp_number: number,
        timezone: tz,
      });
      setMsg("✓ WhatsApp alerts enabled. Daily reminders at 09:00 (boost) and 12:00 (payout) your local time.");
      setNumber("");
      loadStatus();
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not save WhatsApp settings.");
    } finally {
      setBusy(null);
    }
  };

  const sendTest = async () => {
    setErr(null);
    setMsg(null);
    setBusy("test");
    try {
      await axios.post(`${API}/terminal/whatsapp/test`, null, { params: { code } });
      setMsg("Test message sent — check your WhatsApp.");
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not send test message.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-6 border border-[#25D366]/25 bg-surface p-6" data-testid="whatsapp-card">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#25D366]">WhatsApp alerts</p>
        {status?.configured ? (
          <span className="rounded-full bg-[#25D366]/10 px-3 py-1 text-[9px] uppercase tracking-[0.2em] text-[#25D366]"
                data-testid="whatsapp-status-badge">
            Active · ••{status.number_tail}
          </span>
        ) : (
          <span className="rounded-full bg-white/5 px-3 py-1 text-[9px] uppercase tracking-[0.2em] text-white/40"
                data-testid="whatsapp-status-badge">
            Not configured
          </span>
        )}
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-white/55">
        Paste your WhatsApp number below and tap <span className="text-[#25D366]">Enable alerts</span>.
        You'll receive your daily <span className="text-gold">boost reminder at 09:00</span>, your
        <span className="text-gold"> payout countdown at 12:00</span>, plus instant pings on every
        deposit, tier upgrade and payout — all sent from the Aureon desk.
      </p>

      <form onSubmit={save} className="mt-4 space-y-3">
        <div>
          <label className="mb-1 block text-[9px] uppercase tracking-[0.25em] text-white/40">
            Your WhatsApp number (international format)
          </label>
          <input
            data-testid="whatsapp-number-input"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="+27831234567"
            className="w-full border border-white/15 bg-ink px-4 py-3 font-tabular text-xs text-white placeholder:text-white/25 focus:border-[#25D366] focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-[9px] uppercase tracking-[0.25em] text-white/40">
            Your timezone (for daily 09:00 & 12:00 reminders)
          </label>
          <select
            data-testid="whatsapp-tz-select"
            value={tz}
            onChange={(e) => setTz(e.target.value)}
            className="w-full border border-white/15 bg-ink px-4 py-3 font-tabular text-xs text-white focus:border-[#25D366] focus:outline-none"
          >
            {!COMMON_TZS.includes(tz) && <option value={tz}>{tz}</option>}
            {COMMON_TZS.map((z) => (
              <option key={z} value={z}>{z}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="whatsapp-save-btn"
            type="submit"
            disabled={busy === "save" || !number}
            className="rounded-full bg-[#25D366] px-6 py-2.5 text-[10px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-[#1DAE54] disabled:opacity-40"
          >
            {busy === "save" ? "Saving…" : status?.configured ? "Update" : "Enable alerts"}
          </button>
          {status?.configured && (
            <button
              data-testid="whatsapp-test-btn"
              type="button"
              onClick={sendTest}
              disabled={busy === "test"}
              className="rounded-full border border-[#25D366]/40 px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] text-[#25D366] transition-colors hover:bg-[#25D366]/10 disabled:opacity-40"
            >
              {busy === "test" ? "Sending…" : "Send test"}
            </button>
          )}
          {msg && <span className="text-[10px] text-[#25D366]" data-testid="whatsapp-msg">{msg}</span>}
          {err && <span className="text-[10px] text-[#FF003C]" data-testid="whatsapp-err">{err}</span>}
        </div>
      </form>
    </div>
  );
};

export default WhatsAppCard;
